import React, {useContext, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import Dialog from "@mui/material/Dialog";
import {DialogContent, DialogTitle, Grid} from "@mui/material";
import {FormattedMessage} from "react-intl";
import Badge from "../../../../components/Badge";
import {greyishBlue100} from "../../../../assets/jss/main";
import ToggleNavigation from "../../../../components/Toggle/ToggleNavigation";
import Toggle from "../../../../components/Toggle/Toggle";
import CustomButton from "../../../../components/CustomButton";
import {BarCodeScannerContext} from "../../../../contexts/barCodeScannerContext";
import TobeScannedArticle from "./TobeScannedArticle";

ScanModal.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    toBeScannedItems: PropTypes.array.isRequired,
    itemWorkflowUpdate: PropTypes.func.isRequired
};

function ScanModal({open, handleClose, toBeScannedItems = [], itemWorkflowUpdate}) {
    const {scannedString, setEnableScan} = useContext(BarCodeScannerContext)

    const [toggleValue, setToggleValue] = useState(0)
    const [loading, setLoading] = useState(false)
    const [items, setItems] = useState([])
    const [selectedItems, setSelectedItems] = useState([])

    useEffect(() => {
        setItems(prev => {
            const validatedMap = new Map(prev.filter(i => i.validated).map(i => [i['@id'], i]))
            return toBeScannedItems.map(item =>
                validatedMap.has(item['@id'])
                    ? {...item, validated: true, status: validatedMap.get(item['@id']).status, oldStatus: validatedMap.get(item['@id']).oldStatus}
                    : {...item, validated: false}
            )
        })
    }, [toBeScannedItems])

    const markItemAsValidated = (response, originalItem) => {
        setItems(prev => prev.map(item =>
            item['@id'] === response['@id']
                ? {...item, validated: true, status: response.status, oldStatus: originalItem.status}
                : item
        ))
    }

    const handleBatchValidation = () => {
        const selectedIds = new Set(selectedItems.map(i => i['@id']))
        const itemsToValidate = selectedItems.length > 0
            ? items.filter(item => selectedIds.has(item['@id']) && !item.validated)
            : items.filter(item => !item.validated)
        if (itemsToValidate.length === 0) return
        Promise.all(itemsToValidate.map(item =>
            itemWorkflowUpdate(item, false, false).then(response => ({ response, originalItem: item }))
        )).then((results) => {
            results.forEach(({ response, originalItem }) => markItemAsValidated(response, originalItem))
            setSelectedItems([])
            setLoading(false)
        })
    }

    const displayBatchAction = () => {
        if (1 === toggleValue) return false
        return !toBeScannedItems.some(item => item.status !== toBeScannedItems[1].status);
    }

    const selectItem = (item, checked) => {
        setSelectedItems(prev => checked ? [...prev, item] : prev.filter(i => i !== item))
    }

    useEffect(() => {
        if (scannedString && 0 < scannedString.length) {
            setEnableScan(false)
            const scannedItem = items.find(item => item.variation.barcode === scannedString && !item.validated)
            if (scannedItem) {
                setLoading(true)

                itemWorkflowUpdate(scannedItem).then((response) => {
                    markItemAsValidated(response, scannedItem)
                    setLoading(false)
                    setEnableScan(true)
                })
            } else {
                setEnableScan(true)
            }
        }
    }, [scannedString]);

    return (
        <Dialog open={open} onClose={handleClose} fullWidth sx={{maxHeight: '560px', margin: 'auto'}}>
            <DialogTitle sx={{padding: 0}}>
                <Grid container alignItems="center" sx={{borderBottom: '1px solid', borderColor: greyishBlue100, padding: '16px 24px 8px 24px'}}>
                    <span style={{fontWeight: 'bold', marginRight: '8px'}}><FormattedMessage id="retailer.picking.list.scanModal.title" /></span>
                    <Badge label={items.filter(item => item.validated).length + "/" + toBeScannedItems.length } rounded color={items.some(item => !item.validated) ? 'info' : 'success'}/>
                </Grid>
            </DialogTitle>
            <DialogContent>
                <Grid container direction="column" sx={{marginTop: '26px'}}>
                    <Grid container justifyContent={'space-between'} alignItems={'center'} sx={{position: 'sticky', top: 0, backgroundColor: 'white', minHeight: '60px', zIndex: 10}}>
                        <ToggleNavigation
                            value={toggleValue}
                            onChange={(value) => setToggleValue(value)}
                        >
                            <Toggle label={<FormattedMessage id="retailer.picking.list.scanModal.toBeScannedItems" />} count={items.filter(item => !item.validated).length}/>
                            <Toggle label={<FormattedMessage id="retailer.picking.list.scanModal.scannedItems" />} count={items.filter(item => item.validated).length}/>
                        </ToggleNavigation>

                        {displayBatchAction() && (
                            <CustomButton color={'green'} loading={loading} disabled={loading} onClick={() => {
                                setLoading(true)
                                handleBatchValidation()
                            }}>
                                <FormattedMessage id={selectedItems.length === 0 ? "retailer.picking.list.scanModal.batchValidation" : "retailer.picking.list.scanModal.batchValidation.selectedItems"} />
                            </CustomButton>
                        )}
                    </Grid>

                    <Grid
                        container
                        direction="column"
                        flexWrap={'nowrap'}
                        sx={{marginTop: '26px'}}
                    >
                        {items.length > 0 && items.map(item => {
                            if (0 ===toggleValue && item.validated) {
                                return
                            }

                            if (1 ===toggleValue && !item.validated) {
                                return
                            }
                            return (
                                <Grid item>
                                    <TobeScannedArticle article={item} selectItem={selectItem} displayOldStatus={1 === toggleValue}/>
                                </Grid>
                            )
                        })}
                    </Grid>
                </Grid>

            </DialogContent>
        </Dialog>
    );
}

export default ScanModal;